import { LocalStorageService } from '../../../../services/local-storage-service';

const CACHE_KEY = 'shipstation_labs_features_cache';
const CACHE_VERSION = 'v1';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const LabsCache = {
  /**
   * Save Labs data to localStorage cache
   * @param {object} data - The data to cache
   * @param {Array} data.features - Raw API features
   * @param {Array} data.displayableFeatures - Merged features with local constants
   * @param {Set} data.enabledFeatures - Set of enabled feature IDs
   * @param {Array} [sellerFeatures] - Current seller features for change detection
   */
  save: (data, sellerFeatures = []) => {
    const cacheEntry = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      sellerFeaturesSnapshot: sellerFeatures,
      data: {
        features: data.features,
        displayableFeatures: data.displayableFeatures,
        enabledFeatures: Array.from(data.enabledFeatures), // Convert Set to Array for JSON
      },
    };
    LocalStorageService.setItem(CACHE_KEY, cacheEntry);
  },

  /**
   * Load Labs data from localStorage cache
   * @returns {object|null} Cached data or null if not available/invalid
   */
  load: () => {
    const entry = LocalStorageService.getItem(CACHE_KEY, false);
    if (!entry) {
      return null;
    }

    if (entry.version !== CACHE_VERSION) {
      LocalStorageService.removeItem(CACHE_KEY);
      return null;
    }

    const isExpired = Date.now() - entry.timestamp > CACHE_TTL;

    return {
      ...entry.data,
      enabledFeatures: new Set(entry.data.enabledFeatures),
      sellerFeaturesSnapshot: entry.sellerFeaturesSnapshot,
      isExpired,
      timestamp: entry.timestamp,
    };
  },

  clear: () => {
    LocalStorageService.removeItem(CACHE_KEY);
  },

  /**
   * Get cache info for debugging
   * @returns {object} Cache metadata
   */
  getInfo: () => {
    const cached = LabsCache.load();
    if (!cached) {
      return { exists: false };
    }

    return {
      exists: true,
      version: CACHE_VERSION,
      timestamp: cached.timestamp,
      age: Date.now() - cached.timestamp,
      isExpired: cached.isExpired,
      featureCount: cached.features?.length || 0,
      displayableFeatureCount: cached.displayableFeatures?.length || 0,
      enabledFeatureCount: cached.enabledFeatures?.size || 0,
    };
  },
};
